/**
 * New Relic Browser agent initialization.
 *
 * Configured at build time via REACT_APP_NEWRELIC_* env vars sourced from
 * Terraform outputs (newrelic_browser_application_id +
 * newrelic_browser_license_key on the skills-marketplace module). The
 * license key is a Browser-scoped ingest key, intended to be shipped in
 * client JS — it only allows ingesting PageView / JavaScriptError / Ajax /
 * BrowserInteraction events into the matching application.
 *
 * Called once from index.tsx before React renders, so the agent is in
 * place to capture early errors and the initial PageView.
 */

import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import { env } from "./env";

let agent: BrowserAgent | null = null;

/**
 * Resolve the GitHub Proxy's origin so the Browser agent's distributed
 * tracing can propagate W3C `traceparent`/`tracestate` headers to it (see
 * `distributed_tracing.allowed_origins` below). Returns null when the proxy
 * URL env var is unset or unparseable — callers must fall back to a plain
 * `{ enabled: true }` DT config rather than throw, mirroring the "missing
 * config → skip silently" posture used for the NR env vars themselves.
 */
function resolveProxyOrigin(): string | null {
  const proxyUrl = env.REACT_APP_GITHUB_PROXY_URL;
  if (!proxyUrl) return null;
  try {
    return new URL(proxyUrl).origin;
  } catch {
    return null;
  }
}

export function initNewRelic(): void {
  if (agent) return;

  const accountID = env.REACT_APP_NEWRELIC_ACCOUNT_ID;
  const applicationID = env.REACT_APP_NEWRELIC_APPLICATION_ID;
  const licenseKey = env.REACT_APP_NEWRELIC_LICENSE_KEY;
  const trustKey = env.REACT_APP_NEWRELIC_TRUST_KEY || accountID;

  if (!accountID || !applicationID || !licenseKey) {
    // Missing config — local dev or a deploy that hasn't seeded the NR
    // vars yet. Skip silently rather than throwing; the SPA must keep
    // rendering even without observability.
    return;
  }

  const proxyOrigin = resolveProxyOrigin();

  try {
    agent = new BrowserAgent({
      info: {
        beacon: "bam.nr-data.net",
        errorBeacon: "bam.nr-data.net",
        licenseKey,
        applicationID,
        sa: 1,
      },
      init: {
        distributed_tracing: proxyOrigin
          ? {
              enabled: true,
              allowed_origins: [proxyOrigin],
              cors_use_tracecontext_headers: true,
              // The agent adds its proprietary `newrelic` header to CORS
              // requests by default (cors_use_newrelic_header defaults to
              // true) in addition to the W3C headers above. github-proxy's
              // CORS allowlist only permits traceparent/tracestate, so
              // without this the extra header fails preflight and blocks
              // every cross-origin call to the proxy (issue: "Download
              // failed" / check-access CORS errors after #341).
              cors_use_newrelic_header: false,
            }
          : { enabled: true },
        privacy: { cookies_enabled: true },
      },
      loader_config: {
        accountID,
        trustKey,
        agentID: applicationID,
        licenseKey,
        applicationID,
      },
    });
  } catch (err) {
    // CSP violations, browser extension interference, or unexpected
    // agent bugs must not block the SPA from rendering. Leave agent
    // null so reportError() no-ops, but surface the failure so a quiet
    // outage is at least visible in DevTools.
    console.error("[NewRelic] BrowserAgent init failed — error reporting disabled:", err);
    return;
  }

  try {
    // Stamp the ServiceNow CI-ID onto every event the agent reports (PageView,
    // Ajax, BrowserInteraction, JavaScriptError, and distributed-tracing Span),
    // under the attribute key `tags.coxauto_ci-id`. persist=true so it survives
    // route changes within the SPA session. Sourced at build time from Terraform
    // (local.web_tags["coxauto:ci-id"]) via REACT_APP_NEWRELIC_CI_ID — the same
    // CI-ID the backend Lambdas read from NEW_RELIC_CI_ID, so the value stays in
    // lockstep across web and backend instead of drifting. Skip silently when
    // unset (local dev, or a deploy that hasn't seeded the NR vars), matching
    // the missing-config posture used for accountID/applicationID/licenseKey.
    //
    // Why the odd `tags.`-prefixed key: the Reliability Central / PRR
    // "Distributed Tracing" control (rule 4) runs
    //   SELECT count(*) FROM Span WHERE tags.coxauto_ci-id = '<CI>'
    // For APM-instrumented services NR's backend stamps entity tags onto each
    // span as real `tags.*` attributes, so the query matches. The Browser agent
    // emits NO `tags.*` namespace on its spans and NR does not decorate Browser
    // spans with entity tags (verified empirically: a Browser span's keyset has
    // no `tags.*` keys), so tagging the entity in Terraform can never satisfy
    // this check. Setting the literal attribute key here is what puts
    // `tags.coxauto_ci-id` on the Browser spans so the control passes. The
    // matching entity tag (Terraform) still drives the entity-search controls
    // (Transaction Monitoring, SLO).
    const ciId = env.REACT_APP_NEWRELIC_CI_ID;
    if (ciId) {
      agent.setCustomAttribute("tags.coxauto_ci-id", ciId, true);
    }

    // Stamp isSynthetic on every event so the login-gate synthetic monitor's
    // own traffic (infra/modules/newrelic-alerts/synthetics.tf) can be
    // excluded from user-facing metrics that share the same PageView/
    // AjaxRequest stream it relies on for its loss-of-signal floor: the
    // Overview dashboard's "Page views" KPI, the web/page-load-p95 alert, and
    // the web-latency SLO's valid/good events (newrelic-slo.tf). None of
    // those were designed with synthetic traffic in mind, and New Relic has
    // no native attribute distinguishing a Synthetics-driven Browser session
    // from a real one (confirmed against NR's own docs) — this has to be
    // self-reported.
    //
    // navigator.webdriver is the detection signal: a W3C-standard, MDN-
    // documented read-only property that's true whenever the browser is
    // controlled via WebDriver automation (Chrome sets it automatically for
    // --enable-automation/--headless), which is exactly what NR's scripted
    // Chrome monitors use under the hood (their own docs describe the
    // scripting surface as Selenium WebDriver 4.1). Real visitors don't set
    // this; browser automation tooling does, by design of the spec.
    //
    // The "isSynthetic" key here has no compiler tie to its consumer,
    // infra/modules/newrelic-nrql/main.tf's web_exclude_synthetic predicate
    // (`isSynthetic IS NOT true`) -- a typo in either would silently defeat
    // this whole exclusion (passing every test and terraform validate on
    // both sides) and let synthetic traffic back into user-facing PageView/
    // latency/SLO metrics. Keep the two literal strings identical if either
    // is ever touched.
    if (navigator.webdriver) {
      agent.setCustomAttribute("isSynthetic", true, true);
    }
  } catch (err) {
    // A failure here (e.g. setCustomAttribute throwing) is NOT a dead agent --
    // agent is already assigned above, so reportError()/setUser() still work
    // fine. Distinct message from the constructor's catch above so on-call
    // doesn't misdiagnose a cosmetic tag failure as reporting being disabled.
    console.error("[NewRelic] Custom attribute stamping failed (agent still initialized; error reporting unaffected):", err);
  }
}

/**
 * Associate the current session with an authenticated user so New Relic can
 * report Unique Users. Populates `enduser.id` on all subsequent PageView,
 * Ajax, and BrowserInteraction events.
 *
 * Pass the opaque MSAL homeAccountId — not an email — to keep PII off a
 * third-party SaaS. Unique-count queries work identically with opaque IDs:
 *   SELECT uniqueCount(enduser.id) FROM PageView WHERE appName = '...'
 *
 * Silently no-ops when the agent isn't initialized (local dev, missing
 * env vars), so callers don't need to gate on initialization state.
 */
export function setUser(userId: string): void {
  if (!agent) return;
  try {
    agent.setUserId(userId);
  } catch (err) {
    console.error("[NewRelic] setUserId failed:", err);
  }
}

/**
 * Forward a caught error to New Relic so it counts toward the
 * JavaScriptError alert. Without this, .catch(console.error) handlers
 * stay invisible to the agent — only uncaught errors auto-instrument.
 *
 * `context` tags the error with the call site so on-call can route
 * cheaply: "catalog-fetch", "auth-init", "skill-files-fetch", etc.
 *
 * Silently no-ops when the agent isn't initialized (local dev, missing
 * env vars), so callers don't need to gate on initialization state.
 */
export function reportError(
  error: unknown,
  context?: string,
  customAttributes: Record<string, string | number | boolean> = {},
): void {
  if (!agent) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const attrs = context ? { ...customAttributes, context } : customAttributes;

  try {
    agent.noticeError(err, attrs);
  } catch (noticeErr) {
    // Defense-in-depth: noticeError throwing must not break the caller's
    // catch block. Log both errors so a silent reporting outage is at
    // least visible in DevTools.
    console.error("[NewRelic] noticeError failed:", err, noticeErr);
  }
}

/**
 * Record a non-error product event (a PageAction custom event) for things worth
 * measuring but that aren't failures — e.g. a client-side guard blocking a
 * disallowed upload. Deliberately NOT `reportError`: these would otherwise inflate
 * the JavaScriptError alert and page on legitimate user actions.
 *
 * Emitted as an `addPageAction` so it lands in NRDB as a PageAction with the given
 * attributes (query: `SELECT count(*) FROM PageAction WHERE actionName = '...'`),
 * mirroring the backend's `uploadPayloadRejected` log marker so client- and
 * server-side rejection rates are both observable. Pass only bounded attributes
 * (counts, rule classes) — never raw file paths or user content.
 *
 * Silently no-ops when the agent isn't initialized (local dev, missing env vars).
 */
export function reportEvent(
  name: string,
  attributes: Record<string, string | number | boolean> = {},
): void {
  if (!agent) return;
  try {
    agent.addPageAction(name, attributes);
  } catch (err) {
    console.error("[NewRelic] addPageAction failed:", err);
  }
}
